import type { AdminGymCreated } from '@steado/admin-contract/gyms';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Labelled } from '@/components/form/fields';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth/auth-context';

import { createGym, gymTimeZonesQueryOptions } from './gyms-query';
import {
  DEFAULT_TIME_ZONE,
  EMPTY_NEW_GYM,
  fieldIssues,
  toCreateBody,
  type NewGymField,
  type NewGymValues,
} from './new-gym-form';

interface NewGymDialogProps {
  readonly onClose: () => void;
  readonly onCreated: (created: AdminGymCreated) => void;
}

/**
 * Sets up a gym that has written in: the gym, its owner, and the invite the
 * owner gets by email. Owners cannot sign themselves up, so this is the only
 * way a gym starts.
 */
export function NewGymDialog({ onClose, onCreated }: NewGymDialogProps) {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const zones = useQuery(gymTimeZonesQueryOptions(api));
  const [values, setValues] = useState<NewGymValues>(EMPTY_NEW_GYM);
  // A field's problem shows once it has been left, not while the form is still blank.
  const [visited, setVisited] = useState<ReadonlySet<NewGymField>>(new Set());
  const zoneId = useId();

  const save = useMutation({
    mutationFn: () => createGym(api, toCreateBody(values)),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gyms'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
      onCreated(created);
    },
    retry: false,
  });

  const issues = fieldIssues(values);
  const invalid = Object.keys(issues).length > 0;
  const set = (patch: Partial<NewGymValues>): void => {
    setValues((current) => ({ ...current, ...patch }));
  };
  const field = (name: NewGymField) => ({
    value: values[name],
    issue: visited.has(name) ? issues[name] : undefined,
    onChange: (next: string) => {
      set({ [name]: next });
    },
    onBlur: () => {
      setVisited((current) => new Set(current).add(name));
    },
  });

  const zoneOptions = zones.data ?? [{ id: DEFAULT_TIME_ZONE, label: DEFAULT_TIME_ZONE }];

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <DialogContent aria-label="New gym" className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New gym</DialogTitle>
          <DialogDescription>
            For a gym that has written to us. The owner gets an email with a link to set their password, and
            the link works for 7 days.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!invalid && !save.isPending) save.mutate();
          }}
        >
          <TextField label="Gym name" placeholder="Iron Den" autoComplete="off" {...field('gymName')} />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Owner name"
              placeholder="Meera Shah"
              autoComplete="off"
              {...field('ownerName')}
            />
            <TextField
              label="Owner email"
              placeholder="meera@irondengym.in"
              type="email"
              autoComplete="off"
              hint="The invite goes here, and the owner signs in with it."
              {...field('ownerEmail')}
            />
            <TextField
              label="Owner phone (optional)"
              placeholder="+919820011234"
              type="tel"
              autoComplete="off"
              hint="With the country code."
              mono
              {...field('ownerPhone')}
            />
            <TextField
              label="Member limit"
              autoComplete="off"
              inputMode="numeric"
              hint="How many members the gym may have. Trainers don’t count."
              mono
              {...field('memberLimit')}
            />
            <Labelled id={zoneId} label="Time zone" hint="The gym’s days and weeks follow it.">
              <Select
                value={values.timeZone}
                onValueChange={(timeZone) => {
                  set({ timeZone });
                }}
              >
                <SelectTrigger id={zoneId} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {zoneOptions.map((zone) => (
                    <SelectItem key={zone.id} value={zone.id}>
                      {zone.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Labelled>
          </div>

          {save.isError ? (
            <Alert tone="err">
              <AlertDescription>{save.error.message}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" disabled={save.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={invalid || save.isPending}>
              {save.isPending ? 'Creating…' : 'Create gym and send invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface TextFieldProps {
  readonly label: string;
  readonly value: string;
  readonly issue: string | undefined;
  readonly onChange: (value: string) => void;
  readonly onBlur: () => void;
  readonly placeholder?: string;
  readonly hint?: string;
  readonly type?: 'email' | 'tel';
  readonly inputMode?: 'numeric';
  readonly autoComplete?: string;
  readonly mono?: boolean;
}

function TextField({
  label,
  value,
  issue,
  onChange,
  onBlur,
  placeholder,
  hint,
  type,
  inputMode,
  autoComplete,
  mono = false,
}: TextFieldProps) {
  const id = useId();
  const issueId = useId();
  return (
    <Labelled id={id} label={label} {...(issue === undefined && hint !== undefined ? { hint } : {})}>
      <Input
        id={id}
        value={value}
        className={mono ? 'font-mono' : undefined}
        aria-invalid={issue !== undefined}
        {...(issue === undefined ? {} : { 'aria-describedby': issueId })}
        {...(placeholder === undefined ? {} : { placeholder })}
        {...(type === undefined ? {} : { type })}
        {...(inputMode === undefined ? {} : { inputMode })}
        {...(autoComplete === undefined ? {} : { autoComplete })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={onBlur}
      />
      {issue === undefined ? null : (
        <span id={issueId} className="text-xs text-err-fg">
          {issue}
        </span>
      )}
    </Labelled>
  );
}
